import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Stethoscope, ShieldUser } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const links = [
  { href: "#home", label: "Home" },
  { href: "#products", label: "Products" },
  { href: "#catalogue", label: "Catalogue" },
  { href: "#about", label: "About" },
  { href: "#contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onHome = location.pathname === "/";
  const { user } = useAuth();
  const adminHref = user ? "/admin" : "/admin/login";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !onHome
          ? "bg-white/90 backdrop-blur-md border-b border-border shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="navbar-logo"
          className="flex items-center gap-2.5 group"
        >
          <span className="w-10 h-10 rounded-2xl bg-primary text-primary-foreground grid place-items-center shadow-md group-hover:scale-105 transition">
            <Stethoscope className="w-5 h-5" strokeWidth={2.4} />
          </span>
          <span className="font-heading font-bold text-lg sm:text-xl tracking-tight">
            <span className="text-primary">Ayushman</span>
            <span className="text-foreground/80"> Distributors</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <a
              key={l.href}
              href={onHome ? l.href : `/${l.href}`}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="px-4 py-2 text-sm font-medium text-foreground/75 hover:text-primary hover:bg-secondary rounded-full transition"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Link
            to={adminHref}
            data-testid="navbar-admin-shortcut"
            title="Admin Panel"
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-foreground/75 hover:text-primary hover:bg-secondary rounded-full transition border border-border"
          >
            <ShieldUser className="w-4 h-4" />
            Admin
          </Link>
          <Link to="/catalogue" data-testid="navbar-mr-mode-btn">
            <Button className="rounded-full bg-primary hover:bg-primary/90 px-5">
              MR Field Mode
            </Button>
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="md:hidden w-10 h-10 grid place-items-center rounded-full bg-secondary text-primary"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t border-border shadow-lg">
          <div className="px-5 py-4 flex flex-col gap-2">
            {links.map((l) => (
              <a
                key={l.href}
                href={onHome ? l.href : `/${l.href}`}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className="px-4 py-3 text-sm font-medium text-foreground/80 hover:bg-secondary rounded-2xl"
              >
                {l.label}
              </a>
            ))}
            <Link
              to="/catalogue"
              onClick={() => setOpen(false)}
              data-testid="mobile-mr-mode-btn"
            >
              <Button className="w-full rounded-full bg-primary hover:bg-primary/90 mt-2">
                MR Field Mode
              </Button>
            </Link>
            <Link
              to={adminHref}
              onClick={() => setOpen(false)}
              data-testid="mobile-admin-shortcut"
            >
              <Button
                variant="outline"
                className="w-full rounded-full mt-2"
              >
                <ShieldUser className="w-4 h-4 mr-2" />
                Admin Panel
              </Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
